import { Stack, Typography } from '@mui/material';
import { Routes, ZoomLevels } from 'enums';
import useLinkedinCookies from 'hooks/use-linkedin-cookies';
import _ from 'lodash';
import Logger from 'logger/Logger';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';
import JobTitleSuggestionsDialog from '../features/augmented-writing/job-title-suggestion/job-title-suggestion-dialog';
import { useQueryProject } from '../features/augmented-writing/use-query-project';
import PaiButton from '../features/ui/base-components/pai-button';

const AugmentedWritingComplete: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const project = useQueryProject();

  const [copied, setCopied] = useState(false);
  const location = useLocation();

  const { liAtCookie, sessionCookie } = useLinkedinCookies();

  const [jobTitleSuggestionsDialog, setJobTitleSuggestionsDialog] = useState<{ open: boolean }>({ open: false });

  const copyTextToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(project?.text || '');
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      Logger.error('Copy to clipboard not possible', error);
    }
  };

  /**
   * Job title suggestions dialog
   */

  const handleOpenJobTitleSuggestionsDialog = () => {
    setJobTitleSuggestionsDialog({ open: true });
  };

  const handleCloseJobTitleSuggestionsDialog = () => {
    setJobTitleSuggestionsDialog({ open: false });
  };

  return (
    <>
      <Stack className={classes.optimizationDone}>
        <div className={classes.content}>
          <div className={classes.title}>
            <Typography className={classes.titleText}>{t('userDoneTitle')}</Typography>
            <Typography className={classes.titleText}>{t('userDoneText2')}</Typography>
          </div>
          <div className={classes.borderContainer}>
            {!_.isNil(project) ? (
              <div>
                {!_.isNil(project.occupation) ? (
                  <Typography className={classes.shortInstruction}>
                    {t(
                      location.pathname.includes(Routes.HR_BOOSTER)
                        ? 'hrBoosterFinalTextTitle'
                        : location.pathname.includes(Routes.LEAD_BOOSTER)
                        ? 'leadBoosterFinalTextTitle'
                        : 'hrBoosterFinalTextTitle',
                      {
                        occupation: project.occupation,
                      }
                    )}
                  </Typography>
                ) : null}
                {!_.isNil(project.text) ? <Typography className={classes.text}>{project.text}</Typography> : null}
              </div>
            ) : null}
          </div>
          <Stack direction={'row'} justifyContent={'center'} mt={2} gap={2}>
            <PaiButton text={!copied ? t('copyText') : t('textCopied')} action={copyTextToClipboard} size={'large'} />
            {location.pathname.includes(Routes.HR_BOOSTER) && !_.isNil(project) && !_.isNil(project.occupation) ? (
              <PaiButton text={t('similarJobTitles')} action={handleOpenJobTitleSuggestionsDialog} size={'large'} />
            ) : null}
          </Stack>
        </div>
      </Stack>

      {jobTitleSuggestionsDialog.open && !_.isNil(project) && !_.isNil(project.text) ? (
        <JobTitleSuggestionsDialog
          open={jobTitleSuggestionsDialog.open}
          occupation={project.occupation}
          text={project.text}
          liAtCookie={liAtCookie}
          sessionCookie={sessionCookie}
          handleClose={handleCloseJobTitleSuggestionsDialog}
        />
      ) : null}
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  optimizationDone: {
    background: theme.palette.backgrounds.main,
    padding: '16px',
    height: 'calc(100vh - 60px - 32px)',
    width: '100%',

    [theme.breakpoints.down('xl')]: {
      height: `calc((100vh / ${ZoomLevels.XL}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1300)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('lg')]: {
      height: `calc((100vh / ${ZoomLevels.LG}) - 60px - 32px)`,
    },
    [theme.breakpoints.down(1000)]: {
      height: `calc((100vh / ${ZoomLevels.CUSTOM_MD}) - 60px - 32px)`,
    },
    [theme.breakpoints.down('md')]: {
      height: `calc((100vh / ${ZoomLevels.MD}) - 60px - 32px)`,
    },
  },
  content: {
    width: '100%',
    height: '100%',
    background: '#FFF',
    boxShadow: Shadows.GREY_SHADOW_LIGHT,
    padding: '16px',
  },
  title: {
    marginBottom: '32px',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  titleText: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    textAlign: 'center',
    textTransform: 'uppercase',
  },
  borderContainer: {
    padding: '16px',
    border: `2px solid ${theme.palette.backgrounds.main}`,
    height: 'calc(100% - 39px - 96px)',
    overflow: 'auto',
  },
  text: {
    fontSize: theme.typography.pxToRem(13),
    whiteSpace: 'pre-wrap',
  },
  shortInstruction: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    textDecoration: 'underline',
    marginBottom: '24px',
  },
  longInstruction: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    padding: '0 4px',
  },
}));

export default AugmentedWritingComplete;
